// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';
import React from 'react';
import {
    AnalyzerTemplateImportOutput,
    AnalyzerTemplateOutput,
    AnalyzerOutput,
    AdminPermissionsEnum,
} from 'thehive-sdk';
import client from '@Client';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { useState } from 'react';
import { computeEventList } from '@Organisms/AnalyzerTemplates/eventList';
import { THList } from '@Molecules/THList';
import { useGenericQuery } from '@Queries/genericQueries';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { StatusContext } from '@Context/StatusProvider';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import useUserPermissions from '@Hooks/useUserPermissions';
import { ICONS } from '@Constants/iconsApp';
import { AnalyzerTemplateDrawer } from '@Organisms/AnalyzerTemplates/AnalyzerTemplateDrawer/AnalyzerTemplateDrawer';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import { useDrawer } from '@Hooks/useDrawer';
import AnalyzerTemplateImport from '@Organisms/AnalyzerTemplates/AnalyzerTemplateImport';
import { ImportButton } from '@Molecules/FileImportDrawer/FileImportDrawer';

import './AnalyzerTemplates.scss';

interface TreatedAnalyzerOutput extends AnalyzerOutput {
    template?: AnalyzerTemplateOutput;
}

const AnalyzerTemplatesList = (): ReactElement => {
    const i = useLocalMessage();
    const [orderedAnalyzer, setOrderedAnalyzer] = useState<AnalyzerOutput[]>([]);
    const [statusTreated, setStatusTreated] = useState<string>('loading');
    const { version, hasCortex, licenseCapabilities } = useContext(StatusContext);
    const { visible, setVisible } = useDrawer({});

    const cortexCapabilities = useMemo<boolean>(
        () => Object.keys(licenseCapabilities.cortex || {}).includes('cortex'),
        [licenseCapabilities],
    );

    const [libTemplate, setLibTemplate] = useState<Record<string, AnalyzerTemplateOutput>>({});

    const hasPermission = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_ANALYZER_TEMPLATE] });

    const eventList = computeEventList({
        controller: client.AnalyzerTemplateController,
        onError: handleNotificationError,
    });

    const { data: dataAnalyzer, status: statusAnalyzer } = useGenericQuery({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
        fetchOnStart: true,
    });

    const {
        data: dataTemplate,
        refetch,
        status: statusTemplate,
    } = useGenericQuery({
        fetchCallback: () => eventList.onList(),
        key: ['get-all-analyzer-templates'],
        fetchOnStart: statusAnalyzer === 'success',
    });

    const importHandler = useCallback(
        (formData: FormData): Promise<void> =>
            eventList
                .onImport(formData)
                .then((output: AnalyzerTemplateImportOutput) => {
                    const outputStatus: Record<string, string[]> = {
                        success: [],
                        error: [],
                    };

                    Object.entries(output).forEach(([name, isOk]) => {
                        const state = isOk ? 'success' : 'error';
                        outputStatus[state].push(name);
                    });

                    const total = outputStatus.success.length + outputStatus.error.length;

                    if (outputStatus.error.length) {
                        openMessage(
                            'error',
                            i(`notification.analyzer-template.import.error`, {
                                listModel: outputStatus.error.join(', '),
                            }),
                        );
                    }

                    openMessage(
                        'success',
                        i(`notification.analyzer-template.import.success`, {
                            count: outputStatus.success.length,
                            total,
                        }),
                    );

                    refetch();
                })
                .catch(handleNotificationError),
        [eventList, i, refetch],
    );

    const columns = columnsRenderer<TreatedAnalyzerOutput>([
        {
            title: i('analyzerTemplates.list.columns.name'),
            className: 'template-analyzer__column-title custom-table__cell',
            render: (data): ReactElement => (
                <>
                    <p className="template-analyzer__title">{data.name}</p>
                    <p className="template-analyzer__description">{data.description || ''}</p>
                </>
            ),
        },
        {
            className: 'template-analyzer__column-actions custom-table__cell',
            render: (data): ReactElement | null =>
                hasPermission ? (
                    <ActionsList
                        actionList={{
                            edit: {
                                label: libTemplate[data.name]
                                    ? i('analyzerTemplates.list.cta.view')
                                    : i('analyzerTemplates.list.cta.default'),
                                icon: <ICONS.EditIcon />,
                                CustomComponent: (triggerElem) => (
                                    <AnalyzerTemplateDrawer
                                        data={libTemplate[data.name] || { content: '', analyzerId: data.name, id: '' }}
                                        triggerElem={triggerElem}
                                        title={i('analyzerTemplates.drawer.title', { name: data.name })}
                                        mode={libTemplate[data.name]?._id ? 'edit' : 'add'}
                                        onSubmit={(template) => {
                                            const { _id, ...data } = template || {};
                                            return _id && _id?.trim() !== ''
                                                ? eventList.onEdit(data, _id).then(() => {
                                                      openMessage('success', i('notification.analyzer-template.edit'));
                                                      refetch();
                                                  })
                                                : eventList.onCreate(data).then(() => {
                                                      openMessage(
                                                          'success',
                                                          i('notification.analyzer-template.create'),
                                                      );
                                                      refetch();
                                                  });
                                        }}
                                    />
                                ),
                            },
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                hidden: !libTemplate[data.name],
                                message: i('analyzerTemplates.list.delete-confirm'),
                                icon: <ICONS.DeleteIcon />,
                                query: () =>
                                    libTemplate[data.name] &&
                                    eventList.onDelete(libTemplate[data.name]._id).then(() => {
                                        openMessage('success', i('notification.analyzer-template.delete'));
                                        refetch();
                                    }),
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    useEffect(() => {
        if (dataTemplate) {
            setLibTemplate(
                dataTemplate.reduce<Record<string, AnalyzerTemplateOutput>>(
                    (acc, curr) => ({ ...acc, [curr.analyzerId]: curr }),
                    {},
                ),
            );
        }
    }, [dataTemplate]);

    useEffect(() => {
        if (hasCortex) {
            if (dataAnalyzer) {
                setOrderedAnalyzer(Object.values(dataAnalyzer).sort((a, b) => a.name.localeCompare(b.name)));
            }
        } else {
            setOrderedAnalyzer(
                dataTemplate
                    ?.map(({ analyzerId, ...rest }) => ({
                        ...rest,
                        id: rest._id,
                        name: analyzerId,
                        description: '',
                        version: '',
                        dataTypeList: [],
                        cortexIds: [],
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name)) as AnalyzerOutput[],
            );
        }
    }, [dataAnalyzer, hasCortex, dataTemplate]);

    useEffect(() => {
        if (statusAnalyzer !== 'loading' && statusTemplate !== 'loading') {
            setStatusTreated('success');
        }
    }, [statusAnalyzer, statusTemplate]);

    return (
        <>
            {cortexCapabilities ? (
                <>
                    <THList>
                        <THList.Header>
                            <THList.Slot name="MANAGE" className="header-template-analyzer">
                                <ImportButton
                                    onClick={() => setVisible(true)}
                                    label={i('analyzerTemplates.import.label')}
                                />
                            </THList.Slot>
                        </THList.Header>
                        <THList.Content
                            dataSource={orderedAnalyzer}
                            columns={columns}
                            rowKey="id"
                            className="template-analyzer__wrapper"
                            emptyPlaceholder={
                                <DisplayEmptyData
                                    label={i('analyzerTemplates.none')}
                                    spin={statusTreated !== 'success'}
                                />
                            }
                        />
                    </THList>
                    {visible && (
                        <AnalyzerTemplateImport
                            version={version}
                            visible={visible}
                            onVisibleChange={setVisible}
                            onSubmit={importHandler}
                        />
                    )}
                </>
            ) : (
                <THLayout>
                    <THLayout.Content className="analyzer-template-layout">
                        <UnavailableMessage type="capability" />
                    </THLayout.Content>
                </THLayout>
            )}
        </>
    );
};
const path = RouteBuilder.compute('administration.analyzerTemplates');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.analyzerTemplates',
        icon: <ICONS.FileOutlined />,
    },
    content: <AnalyzerTemplatesList />,
    breadcrumb: {
        label: 'breadcrumb.global.analyzerTemplates',
    },
    Context: ListProvider,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_ANALYZER_TEMPLATE],
    space: ['admin'],
    exact: true,
};
